import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import { useAuth } from 'context'
import * as controllers from 'controllers'
import { CompanyAttorneyInformationDto, CompanyDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import AttorneyAddEditDialog from './AttorneyAddEditDialog'

interface AttorneyCardProps {
  company: CompanyDto
}

export default function AttorneyCard({ company }: AttorneyCardProps) {
  const { permissions } = useAuth()

  const [attorneyAddEditDialogOpen, setAttorneyAddEditDialogOpen] = useState<boolean>(false)
  const [companyAttorneyInformation, setCompanyAttorneyInformation] = useState<CompanyAttorneyInformationDto>(new CompanyAttorneyInformationDto())
  const [isGettingAttorneyInformation, setIsGettingAttorneyInformation] = useState<boolean>(true)
  const [isSavingAttorneyInformation, setIsSavingAttorneyInformation] = useState<boolean>(false)

  useEffect(() => {
    if (company.companyGuid) {
      setIsGettingAttorneyInformation(true)
      controllers
        .getCompanyAttorneyInformation(company.companyGuid)
        .then(response => {
          setCompanyAttorneyInformation(response.value)
        })
        .finally(() => {
          setIsGettingAttorneyInformation(false)
        })
    }
  }, [company])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <AttorneyAddEditDialog
          initialValues={companyAttorneyInformation}
          loading={isSavingAttorneyInformation}
          onClose={() => {
            setAttorneyAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsSavingAttorneyInformation(true)
            controllers
              .updateCompanyAttorneyInformation(company.companyGuid!, values)
              .then(response => {
                enqueueSnackbar('Attorney Information Saved Successfully!', { variant: 'success' })
                setCompanyAttorneyInformation(response.value)
                setAttorneyAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsSavingAttorneyInformation(false)
              })
          }}
          open={attorneyAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Attorney Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setAttorneyAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              {isGettingAttorneyInformation && (
                <Grid item container alignItems='center' justifyContent='center' sx={{ mt: 1 }}>
                  <Grid item>
                    <CircularProgress color='primary' size={50} />
                  </Grid>
                </Grid>
              )}

              {!isGettingAttorneyInformation && (
                <LabelField
                  label='Signature File'
                  value={companyAttorneyInformation.attorneySignatureFileName ? companyAttorneyInformation.attorneySignatureFileName : 'No file uploaded'}
                  error={!companyAttorneyInformation.attorneySignatureFileName}
                  onClick={
                    companyAttorneyInformation.attorneySignatureFileName
                      ? () => {
                          controllers.getCompanyAttorneyInformationSignatureFile(company.companyGuid!, companyAttorneyInformation.attorneySignatureFileName!)
                        }
                      : undefined
                  }
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>||This card only shows if the company is marked as an Attorney. ||</ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
